/**
 * ModelViewer Component - FULLY REFACTORED & INTEGRATED
 * 
 * Complete 3D viewer dengan modular architecture
 * - All hooks integrated
 * - All UI components integrated  
 * - 100% MANDIRI - No dependency to original file
 * - Full port dari ModelViewerBACKUP.jsx dengan fitur lengkap
 * 
 * Created: 2025 - River Visual 3D Mockup Engine
 * Last Updated: 2025 - Full Refactor Complete
 */

import React, { useRef, useState, useEffect } from 'react'

// Import CSS Animations
import './src/styles/animations.css'

// Core Hooks
import { useThreeJSScene } from './src/hooks/core/useThreeJSScene'
import { useModelLoader } from './src/hooks/core/useModelLoader'
import { useDevToolsProtection } from './src/hooks/core/useDevToolsProtection'

// Animation Hooks
import { useAnimationPlayer } from './src/hooks/animation/useAnimationPlayer'
import { useCameraAnimation } from './src/hooks/animation/useCameraAnimation'

// Material Hooks
import { useUndoRedo } from './src/hooks/material/useUndoRedo'
import { useMaterialEditor } from './src/hooks/material/useMaterialEditor'
import { useMaterialSelection } from './src/hooks/material/useMaterialSelection'

// Texture Hooks
import { useTextureManager } from './src/hooks/texture/useTextureManager'
import { useUVEditor } from './src/hooks/texture/useUVEditor'
import { useVideoTextures } from './src/hooks/texture/useVideoTextures'
import { useDragDropTexture } from './src/hooks/texture/useDragDropTexture'

// Export Hooks
import { useScreenshot } from './src/hooks/export/useScreenshot'
import { useVideoRecorder } from './src/hooks/export/useVideoRecorder'

// Light Hooks
import { useCustomLights } from './src/hooks/lights/useCustomLights'

// UI Hooks
import { useKeyboardShortcuts } from './src/hooks/ui/useKeyboardShortcuts'
import { useMobileDetection } from './src/hooks/ui/useMobileDetection'
import { usePanelStates } from './src/hooks/ui/usePanelStates'
import { useHoverOutline } from './src/hooks/ui/useHoverOutline'

// Components
import LoadingScreen from './src/components/LoadingScreen'
import ExportPanel from './src/components/Panels/ExportPanel'
import HistoryPanel from './src/components/Panels/HistoryPanel'
import LightsPanel from './src/components/Panels/LightsPanel'
import MaterialPanel from './src/components/Panels/MaterialPanel'
import CameraPanel from './src/components/Panels/CameraPanel'
import BottomControlPanelFull from './src/components/UI/BottomControlPanelFull'
import FloatingButtons from './src/components/UI/FloatingButtons'
import UndoRedoControls from './src/components/UI/UndoRedoControls'
import Toast from './src/components/UI/Toast'
import VisualIndicators from './src/components/Overlays/VisualIndicators'
import UVEditOverlay from './src/components/Overlays/UVEditOverlay'
import RenderingProgressOverlay from './src/components/Overlays/RenderingProgressOverlay'

export default function ModelViewer(props) {
  const {
    backgroundColor = '#1a1a1f',
    showPanel = true,
    showCameraHelpers = true,
    showFloorGrid = true,
    showObjectExtras = true,
    modelFile = '',
    modelUrl = '',
    hdriFile = '',
    hdriIntensity = 1,
    lightIntensity = 1,
    ambientLightIntensity = 0.5,
    autoRotate = false,
    cameraFov = 50,
    cameraNear = 0.01,
    cameraFar = 1000,
    enableShadows = true,
    shadowIntensity = 0.3,
    showGroundPlane = true,
    showDirectionalLights = true,
    enableAutoSmooth = true,
    autoSmoothAngle = 30,
    loadingIconFile = '',
    showLightsPanel = true,
    showCameraPanel = true,
  } = props

  // Refs
  const containerRef = useRef(null)
  const raycasterRef = useRef(null)
  const mouseRef = useRef(null)

  // Export settings state
  const [exportWidth, setExportWidth] = useState(1920)
  const [exportHeight, setExportHeight] = useState(1080)
  const [useCustomResolution, setUseCustomResolution] = useState(false)
  const [lockAspectRatio, setLockAspectRatio] = useState(false)
  const [transparentBackground, setTransparentBackground] = useState(false)
  const [isVideoSettingsExpanded, setIsVideoSettingsExpanded] = useState(false)
  const [videoSyncMode, setVideoSyncMode] = useState('loop')
  const [viewportReady, setViewportReady] = useState(false)
  
  // Recording mode state
  const [recordingMode, setRecordingMode] = useState('real-time') // 'real-time' or 'high-quality'
  
  // Animation panel state
  const [isAnimationPanelOpen, setIsAnimationPanelOpen] = useState(true)
  
  // isDraggingProgressRef untuk smooth timeline scrubbing
  const isDraggingProgressRef = useRef(false)
  
  // Video buffering counter
  const [videosBuffering, setVideosBuffering] = useState(0)

  // Initialize raycaster
  if (!raycasterRef.current && window.THREE_MODULE) {
    const THREE = window.THREE_MODULE
    raycasterRef.current = new THREE.Raycaster()
    mouseRef.current = new THREE.Vector2()
  }

  // Core: Dev tools protection
  // TEMPORARILY DISABLED untuk debugging camera animation
  // useDevToolsProtection()

  // Core: Mobile detection
  const { isMobile } = useMobileDetection()

  // Core: Scene setup
  const scene = useThreeJSScene(containerRef, {
    backgroundColor,
    cameraFov,
    cameraNear,
    cameraFar,
    lightIntensity,
    ambientLightIntensity,
    showFloorGrid,
    showGroundPlane,
    showDirectionalLights,
    enableShadows,
    shadowIntensity,
    autoRotate,
    lockAspectRatio,
    exportWidth,
    exportHeight,
    useCustomResolution,
    transparentBackground,
  })

  // Core: Model loading
  const model = useModelLoader(scene.sceneRef, {
    modelFile,
    modelUrl,
    isReady: scene.isReady,
    enableAutoSmooth,
    autoSmoothAngle,
    hdriIntensity,
  })

  // Animation: Player
  const animation = useAnimationPlayer(model.modelRef, scene.clockRef)

  // Wire animations from model to player (for selector)
  const animations = model.animations || []
  
  // Auto-select first animation when model loads
  React.useEffect(() => {
    if (animations.length > 0 && !animation.selectedAnimation) {
      animation.setSelectedAnimation(animations[0])
    }
  }, [animations, animation.selectedAnimation])

  // Animation: Camera keyframes
  const camera = useCameraAnimation(scene.cameraRef, scene.controlsRef)

  // Expose refs to window for render loop access
  React.useEffect(() => {
    window.SELECTED_CAMERA_REF = model.selectedCameraRef
    window.MODEL_REF = model.modelRef
    window.CAMERA_KEYFRAMES_COUNT = camera.cameraKeyframes.length
  }, [model.selectedCameraRef, model.modelRef, camera.cameraKeyframes.length])

  // Material: Undo/Redo
  const undoRedo = useUndoRedo()

  // Material: Editor
  const material = useMaterialEditor(undoRedo)

  // UI: Panel states
  const panels = usePanelStates()
  const [isCameraPanelOpen, setIsCameraPanelOpen] = React.useState(false)

  // Material: Selection (click/hover)
  const selection = useMaterialSelection(
    {
      containerRef,
      cameraRef: scene.cameraRef,
      sceneRef: scene.sceneRef,
      raycasterRef,
      mouseRef,
      modelRef: model.modelRef,
      selectedCameraRef: model.selectedCameraRef,
      groundPlaneRef: scene.groundPlaneRef,
    },
    undoRedo,
    panels.setIsMaterialPanelOpen,
    panels.setIsHistoryPanelOpen
  )

  // Texture: Manager
  const texture = useTextureManager(model.modelRef)

  // Texture: UV Editor
  const uv = useUVEditor(selection.selectedMaterial)

  // Texture: Video sync
  const video = useVideoTextures(texture.videoElementsRef, animation)

  // Texture: Drag & Drop
  const dragDrop = useDragDropTexture(
    {
      containerRef,
      cameraRef: scene.cameraRef,
      sceneRef: scene.sceneRef,
      modelRef: model.modelRef,
      raycasterRef,
      mouseRef,
      selectedCameraRef: model.selectedCameraRef,
    },
    texture.videoElementsRef,
    videoSyncMode,
    () => {
      console.log('✅ Texture list updated after drag & drop')
    }
  )

  // Export: Screenshot
  const screenshot = useScreenshot(
    {
      rendererRef: scene.rendererRef,
      sceneRef: scene.sceneRef,
      cameraRef: scene.cameraRef,
      selectedCameraRef: model.selectedCameraRef,
      modelRef: model.modelRef,
    },
    {
      useCustomResolution,
      exportWidth,
      exportHeight,
      transparentBackground,
    }
  )

  // Export: Video recorder (with high-quality mode support)
  const recorder = useVideoRecorder(
    {
      rendererRef: scene.rendererRef,
      sceneRef: scene.sceneRef,
      cameraRef: scene.cameraRef,
      selectedCameraRef: model.selectedCameraRef,
      modelRef: model.modelRef,
      mixerRef: animation.mixerRef,
    },
    {
      videoFormat: 'mp4',
      useCustomResolution,
      exportWidth,
      exportHeight,
      videoFrameRate: 30,
      videoDuration: 5,
    }
  )

  // Lights: Custom lights
  const lights = useCustomLights(scene.sceneRef, showObjectExtras)

  // UI: Keyboard shortcuts
  useKeyboardShortcuts({
    onUndo: undoRedo?.undo,
    onRedo: undoRedo?.redo,
    onCaptureKeyframe: () => {
      if (!animation || !camera) return
      const time = animation.animationProgress
        ? (animation.animationProgress / 100) * (animation.animationDuration || camera.cameraAnimationDuration || 10)
        : camera.cameraKeyframes.length * 2
      camera.addCameraKeyframe(time)
    },
    onTransformMode: (mode) => {
      console.log('Transform mode:', mode)
    },
    onUVEditToggle: (enabled) => uv?.setUvEditMode(enabled),
    isRecordingCameraAnimation: camera?.isRecordingCameraAnimation || false,
    uvEditMode: uv?.uvEditMode || false,
    transformEnabled: false,
  })

  // UI: Hover outline visual feedback
  useHoverOutline(scene.sceneRef, dragDrop.hoveredMesh)

  // Viewport ready effect
  React.useEffect(() => {
    if (scene.isReady) {
      const timer = setTimeout(() => setViewportReady(true), 300)
      return () => clearTimeout(timer)
    }
  }, [scene.isReady])

  // ==========================================
  // CAMERA ANIMATION SYSTEM - Timeline Integrated
  // ==========================================
  // CRITICAL FIX: Use inline interpolation (exact port dari backup)
  // Interpolate camera at specific time - inline di component agar access ke refs langsung
  const interpolateCameraAtTime = React.useCallback((currentTime) => {
    // Guard checks dengan optional chaining
    if (!camera?.cameraKeyframesRef?.current) return
    if (camera.cameraKeyframesRef.current.length < 2) return
    if (!scene?.cameraRef?.current) return
    if (!scene?.controlsRef?.current) return
    if (!window.THREE_MODULE) return

    const THREE = window.THREE_MODULE
    const keyframes = camera.cameraKeyframesRef.current

    // Sort keyframes by time
    const sortedKeyframes = [...keyframes].sort((a, b) => a.time - b.time)

    // Find surrounding keyframes
    let prevKf = sortedKeyframes[0]
    let nextKf = sortedKeyframes[sortedKeyframes.length - 1]

    if (currentTime <= prevKf.time) {
      prevKf = sortedKeyframes[0]
      nextKf = sortedKeyframes[0]
    } else if (currentTime >= nextKf.time) {
      prevKf = sortedKeyframes[sortedKeyframes.length - 1]
      nextKf = sortedKeyframes[sortedKeyframes.length - 1]
    } else {
      for (let i = 0; i < sortedKeyframes.length - 1; i++) {
        if (
          currentTime >= sortedKeyframes[i].time &&
          currentTime <= sortedKeyframes[i + 1].time
        ) {
          prevKf = sortedKeyframes[i]
          nextKf = sortedKeyframes[i + 1]
          break
        }
      }
    }

    // Calculate interpolation factor
    let t = 0
    if (nextKf.time !== prevKf.time) {
      t = (currentTime - prevKf.time) / (nextKf.time - prevKf.time)
    }
    const clampedT = Math.max(0, Math.min(1, t))

    // Apply easing
    let easedT = clampedT
    if (camera.cameraInterpolationType === 'smooth') {
      easedT = clampedT * clampedT * (3 - 2 * clampedT)
    } else if (camera.cameraInterpolationType === 'cubic') {
      easedT =
        clampedT < 0.5
          ? 4 * clampedT * clampedT * clampedT
          : 1 - Math.pow(-2 * clampedT + 2, 3) / 2
    }

    const cam = scene.cameraRef.current
    const controls = scene.controlsRef.current

    // Interpolate position
    cam.position.x = prevKf.position.x + (nextKf.position.x - prevKf.position.x) * easedT
    cam.position.y = prevKf.position.y + (nextKf.position.y - prevKf.position.y) * easedT
    cam.position.z = prevKf.position.z + (nextKf.position.z - prevKf.position.z) * easedT

    // Interpolate quaternion
    const prevQuat = new THREE.Quaternion(
      prevKf.quaternion.x,
      prevKf.quaternion.y,
      prevKf.quaternion.z,
      prevKf.quaternion.w
    )
    const nextQuat = new THREE.Quaternion(
      nextKf.quaternion.x,
      nextKf.quaternion.y,
      nextKf.quaternion.z,
      nextKf.quaternion.w
    )
    cam.quaternion.slerpQuaternions(prevQuat, nextQuat, easedT)

    // Interpolate target
    controls.target.x = prevKf.target.x + (nextKf.target.x - prevKf.target.x) * easedT
    controls.target.y = prevKf.target.y + (nextKf.target.y - prevKf.target.y) * easedT
    controls.target.z = prevKf.target.z + (nextKf.target.z - prevKf.target.z) * easedT

    // Interpolate FOV
    cam.fov = prevKf.fov + (nextKf.fov - prevKf.fov) * easedT
    cam.updateProjectionMatrix()
    controls.update()
  }, [camera.cameraInterpolationType])

  // Register camera interpolation callback - dipanggil dari animation loop!
  React.useEffect(() => {
    // Register callback function
    window.CAMERA_INTERPOLATION_CALLBACK = (currentTime, progress) => {
      // DEBUG: Check guard conditions
      const hasEnoughKeyframes = camera.cameraKeyframes.length >= 2
      const isRecording = camera.isRecordingCameraAnimation
      
      console.log('🎬 Camera callback triggered:', {
        time: currentTime.toFixed(2),
        progress: progress.toFixed(1),
        keyframes: camera.cameraKeyframes.length,
        hasEnough: hasEnoughKeyframes,
        isRecording,
        willInterpolate: hasEnoughKeyframes && !isRecording,
      })

      // Only interpolate when we have keyframes and not recording
      if (!hasEnoughKeyframes || isRecording) {
        console.log('⚠️ Skipping interpolation (guard check failed)')
        return
      }

      console.log('✅ Calling interpolateCameraAtTime...')
      // Call inline interpolation function
      interpolateCameraAtTime(currentTime)
      console.log('✅ interpolateCameraAtTime completed')
    }

    console.log('✅ Camera interpolation callback registered')

    // Cleanup
    return () => {
      window.CAMERA_INTERPOLATION_CALLBACK = null
      console.log('🧹 Camera interpolation callback removed')
    }
  }, [
    camera.cameraKeyframes.length,
    camera.isRecordingCameraAnimation,
    interpolateCameraAtTime,
  ])

  // Render
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* Loading Screen */}
      <LoadingScreen
        allAssetsLoaded={true} // Simplified
        loadingIconFile={loadingIconFile}
        loadingStatus={model.loadingStatus}
        currentAssetLoading={model.currentAssetLoading}
        currentAssetProgress={model.currentAssetProgress}
      />

      {/* 3D Viewport Container */}
      <div
        ref={containerRef}
        onClick={selection.handleMeshClick}
        onDragEnter={dragDrop.handleDragEnter}
        onDragOver={dragDrop.handleDragOver}
        onDragLeave={dragDrop.handleDragLeave}
        onDrop={dragDrop.handleDrop}
        style={{
          width: '100%',
          height: '100%',
          cursor: dragDrop.isDragging
            ? 'copy'
            : selection.selectedMaterial
              ? 'pointer'
              : 'default',
          backgroundColor: transparentBackground
            ? '#cccccc'
            : backgroundColor,
          backgroundImage: transparentBackground
            ? 'linear-gradient(45deg, #999999 25%, transparent 25%), linear-gradient(-45deg, #999999 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #999999 75%), linear-gradient(-45deg, transparent 75%, #999999 75%)'
            : 'none',
          backgroundSize: transparentBackground ? '20px 20px' : 'auto',
          backgroundPosition: transparentBackground
            ? '0 0, 0 10px, 10px -10px, -10px 0px'
            : '0 0',
        }}
      />

      {/* ===== FLOATING PANELS ===== */}

      {/* Export Panel */}
      <ExportPanel
        isOpen={panels.isExportPanelOpen}
        onClose={() => panels.setIsExportPanelOpen(false)}
        isMobile={isMobile}
        viewportReady={viewportReady}
        lockAspectRatio={lockAspectRatio}
        setLockAspectRatio={setLockAspectRatio}
        useCustomResolution={useCustomResolution}
        setUseCustomResolution={setUseCustomResolution}
        transparentBackground={transparentBackground}
        setTransparentBackground={setTransparentBackground}
        exportWidth={exportWidth}
        setExportWidth={setExportWidth}
        exportHeight={exportHeight}
        setExportHeight={setExportHeight}
        isVideoSettingsExpanded={isVideoSettingsExpanded}
        setIsVideoSettingsExpanded={setIsVideoSettingsExpanded}
        videoSyncMode={videoSyncMode}
        setVideoSyncMode={setVideoSyncMode}
        videoElementsRef={texture.videoElementsRef}
        animationDuration={animation.animationDuration}
        captureScreenshot={screenshot.captureScreenshot}
        startRecording={recorder.startRecording}
        stopRecording={recorder.stopRecording}
        isRecording={recorder.isRecording}
        recordingProgress={recorder.recordingProgress}
      />

      {/* History Panel */}
      <HistoryPanel
        isOpen={panels.isHistoryPanelOpen}
        onClose={() => panels.setIsHistoryPanelOpen(false)}
        snapshotCacheRef={undoRedo.snapshotCacheRef}
        snapshotIndexRef={undoRedo.snapshotIndexRef}
        restoreMaterialSnapshot={undoRedo.restoreMaterialSnapshot}
        setSelectedMaterial={selection.setSelectedMaterial}
        setSnapshotTrigger={undoRedo.setSnapshotTrigger}
      />

      {/* Lights Panel */}
      <LightsPanel
        isOpen={panels.isLightsPanelOpen}
        onClose={() => panels.setIsLightsPanelOpen(false)}
        viewportReady={viewportReady}
        isMobile={isMobile}
        customLights={lights.customLights}
        selectedLight={lights.selectedLight}
        setSelectedLight={lights.setSelectedLight}
        addCustomLight={lights.addCustomLight}
        updateCustomLight={lights.updateCustomLight}
        removeCustomLight={lights.removeCustomLight}
      />

      {/* Material Panel */}
      <MaterialPanel
        isOpen={panels.isMaterialPanelOpen}
        onClose={() => panels.setIsMaterialPanelOpen(false)}
        selectedMaterial={selection.selectedMaterial}
        onMaterialChange={selection.setSelectedMaterial}
        saveSnapshot={undoRedo.saveSnapshot}
      />

      {/* Camera Panel */}
      <CameraPanel
        isOpen={isCameraPanelOpen}
        onClose={() => setIsCameraPanelOpen(false)}
        isMobile={isMobile}
        viewportReady={viewportReady}
        cameras={model.cameras}
        selectedCamera={model.selectedCamera}
        setSelectedCamera={model.setSelectedCamera}
        selectedCameraRef={model.selectedCameraRef}
      />

      {/* ===== UI CONTROLS ===== */}

      {/* Floating Toggle Buttons (Top Left) */}
      <FloatingButtons
        viewportReady={viewportReady}
        showPanel={showPanel}
        isExportPanelOpen={panels.isExportPanelOpen}
        setIsExportPanelOpen={panels.setIsExportPanelOpen}
        isLightsPanelOpen={panels.isLightsPanelOpen}
        setIsLightsPanelOpen={panels.setIsLightsPanelOpen}
        isMaterialPanelOpen={panels.isMaterialPanelOpen}
        setIsMaterialPanelOpen={panels.setIsMaterialPanelOpen}
        isCameraPanelOpen={isCameraPanelOpen}
        setIsCameraPanelOpen={setIsCameraPanelOpen}
        showLightsPanel={showLightsPanel}
        showCameraPanel={showCameraPanel}
        cameras={model.cameras}
        selectedCamera={model.selectedCamera}
        setSelectedCamera={model.setSelectedCamera}
        selectedCameraRef={model.selectedCameraRef}
      />

      {/* Undo/Redo Controls (Top Right) */}
      <UndoRedoControls
        showPanel={showPanel}
        isReady={scene.isReady}
        undoRedo={undoRedo}
        snapshotCacheRef={undoRedo.snapshotCacheRef}
        snapshotIndexRef={undoRedo.snapshotIndexRef}
        isHistoryPanelOpen={panels.isHistoryPanelOpen}
        setIsHistoryPanelOpen={panels.setIsHistoryPanelOpen}
      />

      {/* Bottom Control Panel - EXACT PORT dari backup */}
      <BottomControlPanelFull
        // Display controls
        showPanel={showPanel}
        viewportReady={viewportReady}
        isAnimationPanelOpen={isAnimationPanelOpen}
        setIsAnimationPanelOpen={setIsAnimationPanelOpen}
        isMobile={isMobile}
        isReady={scene.isReady}
        modelFile={modelFile}
        modelUrlProp={modelUrl}
        
        // Animation data
        animations={animations}
        selectedAnimation={animation.selectedAnimation}
        setSelectedAnimation={animation.setSelectedAnimation}
        
        // Playback controls
        isPlaying={animation.isPlaying}
        setIsPlaying={animation.setIsPlaying}
        restartAnimation={animation.restartAnimation}
        animationSpeed={animation.animationSpeed}
        setAnimationSpeed={animation.setAnimationSpeed}
        animationProgress={animation.animationProgress}
        animationDuration={animation.animationDuration}
        seekAnimation={animation.seekAnimation}
        isDraggingProgressRef={isDraggingProgressRef}
        currentActionRef={animation.currentActionRef}
        
        // Recording
        isRecording={recorder.isRecording}
        isRendering={recorder.isRendering}
        renderProgress={recorder.recordingProgress}
        recordingMode={recordingMode}
        setRecordingMode={setRecordingMode}
        startRecording={() => {
          if (recordingMode === 'high-quality') {
            recorder.startHighQualityRecording(
              animation.selectedAnimation,
              animation.animationSpeed,
              camera.cameraKeyframes,
              camera.interpolateCamera
            )
          } else {
            recorder.startRecording(animation.selectedAnimation, animation.animationSpeed)
          }
        }}
        stopRecording={recorder.stopRecording}
        cancelRendering={recorder.cancelRendering}
        transparentBackground={transparentBackground}
        
        // Screenshot
        captureScreenshot={screenshot.captureScreenshot}
        
        // Camera Animation
        cameraKeyframes={camera.cameraKeyframes}
        setCameraKeyframes={camera.setCameraKeyframes}
        cameraKeyframesRef={camera.cameraKeyframesRef}
        isRecordingCameraAnimation={camera.isRecordingCameraAnimation}
        setIsRecordingCameraAnimation={camera.setIsRecordingCameraAnimation}
        selectedCameraKeyframe={camera.selectedCameraKeyframe}
        setSelectedCameraKeyframe={camera.setSelectedCameraKeyframe}
        flashKeyframe={camera.flashKeyframe}
        setFlashKeyframe={camera.setFlashKeyframe}
        cameraInterpolationType={camera.cameraInterpolationType}
        setCameraInterpolationType={camera.setCameraInterpolationType}
        cameraAnimationDuration={camera.cameraAnimationDuration}
        cameraRef={scene.cameraRef}
        controlsRef={scene.controlsRef}
        showCameraAnimToast={camera.showCameraAnimToast}
        addCameraKeyframe={camera.addCameraKeyframe}
        updateCameraKeyframe={camera.updateCameraKeyframe}
        deleteCameraKeyframe={camera.deleteCameraKeyframe}
      />

      {/* Toast Notification */}
      <Toast
        toast={camera.cameraAnimToast}
        onClose={() => camera.setCameraAnimToast(null)}
      />

      {/* Visual Indicators (Buffering, Aspect Ratio) */}
      <VisualIndicators
        isReady={scene.isReady}
        viewportReady={viewportReady}
        videosBuffering={videosBuffering}
        lockAspectRatio={lockAspectRatio}
        exportWidth={exportWidth}
        exportHeight={exportHeight}
      />

      {/* UV Edit Mode Overlay */}
      <UVEditOverlay
        uvEditMode={uv.uvEditMode}
        selectedTexture={selection.selectedMaterial}
        uvTransform={uv.materialUvTransform}
        uvTransformMode={uv.materialUvTransformMode}
        setUvTransformMode={uv.setMaterialUvTransformMode}
        onExit={() => uv.setUvEditMode(false)}
      />

      {/* High-Quality Rendering Progress Overlay */}
      <RenderingProgressOverlay
        isRendering={recorder.isRendering}
        renderingPhase={recorder.renderingPhase}
        renderProgress={recorder.recordingProgress}
        videoFrameRate={30}
        videoDuration={5}
        onCancel={recorder.cancelRendering}
      />

      {/* ===== INFO OVERLAYS ===== */}

      {/* No Model Loaded */}
      {scene.isReady && !model.model && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: '#fff',
            zIndex: 100,
          }}
        >
          <h2 style={{ fontSize: '32px', marginBottom: '16px' }}>
            River Visual 3D Engine
          </h2>
          <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.7)' }}>
            Upload a GLB/GLTF model to begin
          </p>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '8px' }}>
            Set modelUrl prop to load 3D model
          </p>
        </div>
      )}

      {/* Info Badge (Top Left) */}
      {scene.isReady && model.model && (
        <div
          style={{
            position: 'absolute',
            top: '20px',
            left: showPanel && viewportReady ? '88px' : '20px',
            padding: '10px 14px',
            background: 'rgba(20, 20, 30, 0.8)',
            backdropFilter: 'blur(10px)',
            color: '#fff',
            borderRadius: '8px',
            fontSize: '11px',
            zIndex: 10,
            transition: 'all 0.3s',
            animation: 'fadeIn 0.5s ease-out',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '4px', opacity: 0.9 }}>
            ✨ Refactored & Modular
          </div>
          <div style={{ opacity: 0.6, fontSize: '10px' }}>
            {model.animations.length} animations • {model.cameras.length} cameras
          </div>
        </div>
      )}

      {/* Error Display */}
      {(scene.error || model.error) && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '20px',
            background: 'rgba(239, 68, 68, 0.9)',
            color: '#fff',
            borderRadius: '8px',
            textAlign: 'center',
            zIndex: 1000,
          }}
        >
          <p style={{ margin: 0, fontSize: '14px' }}>
            ❌ {scene.error || model.error}
          </p>
        </div>
      )}
    </div>
  )
}

// Default props
ModelViewer.defaultProps = {
  backgroundColor: '#1a1a1f',
  showPanel: true,
  showCameraHelpers: true,
  showFloorGrid: true,
  showObjectExtras: true,
  modelFile: '',
  modelUrl: '',
  hdriFile: '',
  hdriIntensity: 1,
  lightIntensity: 1,
  ambientLightIntensity: 0.5,
  autoRotate: false,
  cameraFov: 50,
  cameraNear: 0.01,
  cameraFar: 1000,
  enableShadows: true,
  shadowIntensity: 0.3,
  showGroundPlane: true,
  showDirectionalLights: true,
  enableAutoSmooth: true,
  autoSmoothAngle: 30,
  showLightsPanel: true,
  showCameraPanel: true,
  loadingIconFile: '',
}

// Note: Framer Property Controls dihapus karena ini standalone React app
// Untuk Framer integration, uncomment code di bawah dan install framer-motion

/*
// Framer Property Controls - Uncomment jika deploy ke Framer
import { addPropertyControls, ControlType } from "framer"

addPropertyControls(ModelViewer, {
  modelFile: {
    type: ControlType.File,
    title: "📁 Upload GLB File",
    allowedFileTypes: ["glb", "gltf"],
  },
  // ... rest of property controls
})
*/
